import React, { useState } from "react";
import {
  MdCheck,
  MdChevronLeft,
  MdChevronRight,
  MdAutoAwesome,
  MdInfo,
} from "react-icons/md";
import ZoomButton from "../buttons/ZoomButton";
import EditButton from "../buttons/EditButton";
import Toggle from "../Toggle/Toggle";

const ProductCard = ({
  title,
  sku,
  price,
  images = [],
  generationCount = 0,
  onSelect,
  selectedProduct,
  bIsActive = true,
  canToggle = false,
  onToggle,
  canEdit = false,
  onEdit,
  onZoom,
}) => {
  // Use React state to cycle images instead of Swiper for lightweight catalog rendering
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFullTitle, setShowFullTitle] = useState(false);

  const isActive = selectedProduct?.title === title;

  const changeImage = (getNextIndex) => {
    if (images.length <= 1) return;
    setCurrentImageIndex(getNextIndex);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    changeImage((prev) => (prev + 1) % images.length);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    changeImage((prev) => (prev - 1 + images.length) % images.length);
  };

  const currentImg = images[currentImageIndex] || "";
  const hasMultipleImages = images.length > 1;

  // Derive generation count styling and icon
  const genCount = Number(generationCount) || 0;
  const isHighGen = genCount >= 4;
  const hasGenerations = genCount > 0;
  const GenIcon = isHighGen ? MdAutoAwesome : MdInfo;
  const genColor = isHighGen
    ? "text-primary font-bold"
    : hasGenerations
      ? "text-secondary font-medium"
      : "text-slate-400";
  const generationsText = hasGenerations
    ? `${genCount} look${genCount > 1 ? "s" : ""} generated`
    : "No looks yet";

  return (
    <div
      className="inline-block w-44 shrink-0 select-none"
      onClick={onSelect}
    >
      <div
        className={`app-card app-card-hover w-full relative overflow-hidden transition-all text-left group cursor-pointer ${
          isActive
            ? "border-primary border-2 shadow-[0_0_0_3px_rgba(26,26,26,0.06),0_8px_16px_-4px_rgba(26,26,26,0.04)]"
            : "border-outline hover:border-slate-300"
        }`}
      >
        {/* Card Image area */}
        <div className="w-full aspect-[4/5] bg-surface-container overflow-hidden relative flex items-center justify-center">
          {currentImg ? (
            <img
              className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
              src={currentImg}
              alt={title}
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">
              No Image
            </div>
          )}

          {/* Selected Checkmark Overlay */}
          {isActive && (
            <span className="absolute top-2 left-2 z-10 w-5 h-5 bg-primary text-on-primary rounded-full flex items-center justify-center shadow-sm animate-fade-in">
              <MdCheck size={12} />
            </span>
          )}

          {/* Left Arrow Button */}
          {hasMultipleImages && (
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-5.5 h-5.5 rounded-full bg-white/90 backdrop-blur-sm text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-all cursor-pointer"
              aria-label="Previous image"
            >
              <MdChevronLeft size={16} />
            </button>
          )}

          {/* Right Arrow Button */}
          {hasMultipleImages && (
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-5.5 h-5.5 rounded-full bg-white/90 backdrop-blur-sm text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-all cursor-pointer"
              aria-label="Next image"
            >
              <MdChevronRight size={16} />
            </button>
          )}

          {/* Swiper Dots page indicators */}
          {hasMultipleImages && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
              {images.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                    idx === currentImageIndex ? "bg-primary" : "bg-outline-variant"
                  }`}
                />
              ))}
            </div>
          )}

          {/* Edit Button - Visible in Admin mode on top right */}
          {canEdit && onEdit && (
            <EditButton
              onClick={onEdit}
              positionClasses="absolute top-2 right-2.5"
              size="md"
            />
          )}

          {/* Zoom Button - Always Visible Bottom Right */}
          <ZoomButton
            modalTarget="#productZoomModal"
            onClick={() => onZoom && onZoom(currentImg)}
            positionClasses="absolute bottom-2 right-2.5"
            size="md"
          />
        </div>

        {/* Text descriptions */}
        <div className="p-2.5 border-t border-slate-200/70 bg-white flex flex-col gap-1">
          {/* Row 1: Full Row Title */}
          <p
            className="my-0.5 text-xs font-semibold text-primary cursor-pointer leading-[20px] max-h-[40px] overflow-hidden transition-all duration-200 w-full"
            style={{
              display: showFullTitle ? "block" : "-webkit-box",
              WebkitLineClamp: showFullTitle ? "none" : 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              maxHeight: showFullTitle ? "none" : "40px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowFullTitle((prev) => !prev);
            }}
            title={title}
          >
            {title}
          </p>

          {/* Row 2: SKU ID and Price */}
          {(sku || (price !== undefined && price !== null && price !== "")) && (
            <div className="flex items-center justify-between gap-1 text-[10px] text-slate-500 font-medium">
              <span className="truncate font-mono" title={sku ? `SKU: ${sku}` : ""}>
                {sku ? `SKU: ${sku}` : ""}
              </span>
              {price !== undefined && price !== null && price !== "" && (
                <span className="font-bold text-slate-700 shrink-0">
                  {typeof price === "number" || (!String(price).startsWith("$") && !String(price).startsWith("₹"))
                    ?  `₹${price}`
                    :  `₹ ${price}`}
                </span>
              )}
            </div>
          )}

          {/* Row 3: Generation Count & Toggle */}
          <div className="flex items-center justify-between gap-1 pt-1 border-t border-slate-100">
            <div className={`flex items-center gap-1 text-[10px] ${genColor} font-medium min-w-0 truncate`}>
              <GenIcon size={12} className="shrink-0" />
              <span className="truncate">{generationsText}</span>
            </div>
            {canToggle && onToggle && (
              <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                <Toggle
                  checked={bIsActive}
                  onChange={onToggle}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
